import React from 'react';
import { Alert } from '@mui/material';
import { Button, ErrorIcon, Input, Select } from '../../../components';
import { AvatarUploader } from './components';
import { useProfileEdit } from './useProfileEdit';
import { HeartLoading } from '../../../components/HeartLoading';

type ProfileEditViewProps = Omit<ReturnType<typeof useProfileEdit>, 'metaTags'>;

export const ProfileEditView: React.FC<ProfileEditViewProps> = ({
    profile,
    fileInputRef,
    tempPicture,
    picture,
    submitError,
    formState,
    values,
    handleSubmit,
    isPending,
    isDirty,
    onSubmit,
    handleClickChooseFile,
    onFileChange,
    imageReset,
    handleError,
    onCancelClick,
    dismissAlert,
    stateOptions,
    emailField,
    phoneField,
    streetField,
    cityField,
    stateField,
    zipField,
    profileLoading,
}) => {
    if (profileLoading) {
        return <HeartLoading maxWidth={300} />;
    }
    return (
        <div
            data-testid="profile-edit"
            className="rounded-[32px] shadow-high bg-white w-full sm:ml-8 sm:mr-0 mx-4 overflow-hidden max-w-[792px]"
        >
            {submitError ? (
                <Alert
                    data-testid="profile-edit-error-alert"
                    severity="error"
                    onClose={dismissAlert}
                    iconMapping={{ error: <ErrorIcon /> }}
                >
                    {submitError}
                </Alert>
            ) : null}
            <div
                data-testid="profile-edit-body"
                className="w-full flex flex-col items-center p-6"
            >
                <div
                    data-testid="profile-edit-title"
                    className="text-heading-large text-primary mb-8"
                >
                    Edit Profile Details
                </div>
                <form
                    data-testid="profile-edit-form"
                    className="flex flex-col gap-4 max-w-[335px] w-full"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <AvatarUploader
                        picture={picture}
                        tempPicture={tempPicture}
                        fileInputRef={fileInputRef}
                        onFileChange={onFileChange}
                        imageReset={imageReset}
                        handleClickChooseFile={handleClickChooseFile}
                        handleError={handleError}
                    />
                    <div
                        data-testid="profile-edit-name"
                        className="text-label-medium text-primary"
                    >
                        {profile?.donor.name}
                    </div>
                    <Input
                        data-testid="profile-edit-email"
                        label="Email Address"
                        value={values.email}
                        {...emailField}
                        disabled
                    />
                    <Input
                        data-testid="profile-edit-phone"
                        label="Phone Number"
                        value={values.phone}
                        {...phoneField}
                        error={!!formState.errors.phone}
                        helperText={formState.errors.phone?.message}
                    />
                    <div
                        data-testid="profile-edit-billing-label"
                        className="text-label-medium-light text-primary mt-4"
                    >
                        Billing Info
                    </div>
                    <Input
                        data-testid="profile-edit-street"
                        label="Street Address"
                        value={values.street}
                        {...streetField}
                    />
                    <Input
                        data-testid="profile-edit-city"
                        label="City"
                        value={values.city}
                        {...cityField}
                    />
                    <div
                        data-testid="profile-edit-state-zip"
                        className="flex gap-4"
                    >
                        <Select
                            data-testid="profile-edit-state"
                            label="State"
                            value={values.state}
                            options={stateOptions}
                            {...stateField}
                        />
                        <Input
                            data-testid="profile-edit-zip"
                            label="Zip code"
                            value={values.zip}
                            {...zipField}
                            error={!!formState.errors.zip}
                            helperText={formState.errors.zip?.message}
                        />
                    </div>
                    <div
                        data-testid="profile-edit-actions"
                        className="flex flex-col gap-8 my-4"
                    >
                        <Button
                            data-testid="profile-edit-save"
                            type="submit"
                            disabled={!isDirty}
                            loading={isPending}
                        >
                            Save Profile Information
                        </Button>
                        <Button
                            data-testid="profile-edit-cancel"
                            variant="link"
                            onClick={onCancelClick}
                        >
                            Cancel
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};
